import { supabase } from './supabase';

// ============================================
// TYPES
// ============================================

export interface Dashboard {
  id: string;
  household_id: string;
  name: string;
  description?: string;
  icon: string;
  screen_id?: string;
  is_default: boolean;
  grid_columns: number;
  grid_row_height: number;
  gap: number;
  background_type: 'color' | 'gradient' | 'image' | 'slideshow';
  background_value: string;
  background_slideshow_config?: SlideshowConfig;
  auto_scroll: boolean;
  refresh_interval: number;
  created_by?: string;
  is_locked: boolean;
  pin_code?: string;
  created_at: string;
  updated_at: string;
}

export interface SlideshowConfig {
  source: 'local' | 'google_photos';
  album_id?: string;
  interval_seconds: number;
  transition: 'fade' | 'slide' | 'crossfade';
  shuffle: boolean;
  preload_count: number;
}

export interface SlideshowPhoto {
  photo_url: string;
  thumbnail_url: string | null;
  sort_order: number;
}

export interface DashboardWidget {
  id: string;
  dashboard_id: string;
  widget_type: string;
  grid_x: number;
  grid_y: number;
  grid_w: number;
  grid_h: number;
  config: Record<string, unknown>;
  title?: string;
  show_title: boolean;
  background_color?: string;
  border_radius: number;
  is_visible: boolean;
  visible_start_time?: string;
  visible_end_time?: string;
  visible_days?: number[];
  z_index: number;
  sort_order?: number; // Order within parent section/column
  parent_widget_id?: string | null; // For container sections
  column_index?: number | null; // Which column within a section (0-based)
  min_height?: number | null; // Optional fixed height in pixels
  created_at: string;
  updated_at: string;
}

// Section widget configuration
export interface SectionConfig {
  label: string;
  // Column configuration - supports two modes:
  columnCount?: number;    // Equal-width columns: 3 = three 4-unit columns
  columns?: number[];      // Custom widths: [6, 3, 3] = 50% + 25% + 25%
  // Column widths are in 1-12 grid units (should total 12)
  gap?: number | string;   // Gap between columns - number (px) or CSS string (e.g. "1rem"), default 16
  collapsible?: boolean;   // Default true
  defaultExpanded?: boolean; // Default true
  // Visual styling options
  showTitle?: boolean;     // Show/hide section title, default true
  showDivider?: boolean;   // Show/hide divider line, default true
  backgroundColor?: string; // Section background color
  shadow?: 'none' | 'sm' | 'md' | 'lg'; // Shadow level, default 'none'
  padding?: number | string; // Inner padding - number (px) or CSS string (e.g. "1rem"), default 16
  verticalAlign?: 'top' | 'middle' | 'bottom'; // Vertical alignment of widgets, default 'top'
}

export interface WidgetDefinition {
  id: string;
  name: string;
  description?: string;
  icon: string;
  category: WidgetCategory;
  min_width: number;
  min_height: number;
  max_width: number;
  max_height: number;
  default_width: number;
  default_height: number;
  config_schema: Record<string, unknown>;
  requires_integration?: string[];
  requires_subscription?: string;
  is_active: boolean;
}

export type WidgetCategory =
  | 'time' | 'calendar' | 'tasks' | 'family'
  | 'media' | 'info' | 'smart_home' | 'travel' | 'custom' | 'layout';

export interface DashboardTemplate {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  preview_image_url?: string;
  category: 'family' | 'kitchen' | 'office' | 'kids' | 'minimal' | 'custom';
  grid_columns: number;
  grid_row_height: number;
  background_type: string;
  background_value: string;
  widgets: Array<{
    widget_type: string;
    grid_x: number;
    grid_y: number;
    grid_w: number;
    grid_h: number;
    config?: Record<string, unknown>;
    title?: string;
  }>;
  is_system: boolean;
}

export interface DashboardFull {
  dashboard: Dashboard;
  widgets: DashboardWidget[];
}

// ============================================
// DASHBOARD CRUD
// ============================================

export async function getDashboards(householdId: string): Promise<Dashboard[]> {
  const { data, error } = await supabase
    .from('dashboards')
    .select('*')
    .eq('household_id', householdId)
    .order('is_default', { ascending: false })
    .order('name');

  if (error) {
    console.error('Failed to get dashboards:', error);
    return [];
  }

  return data || [];
}

export async function getDashboard(dashboardId: string): Promise<Dashboard | null> {
  const { data, error } = await supabase
    .from('dashboards')
    .select('*')
    .eq('id', dashboardId)
    .single();

  if (error) {
    console.error('Failed to get dashboard:', error);
    return null;
  }

  return data;
}

export async function getDashboardFull(dashboardId: string): Promise<DashboardFull | null> {
  const { data, error } = await supabase
    .rpc('get_dashboard_full', { p_dashboard_id: dashboardId });

  if (error || !data) {
    console.error('Failed to get full dashboard:', error);
    return null;
  }

  return data as DashboardFull;
}

export async function getDefaultDashboard(householdId: string): Promise<Dashboard | null> {
  const { data, error } = await supabase
    .from('dashboards')
    .select('*')
    .eq('household_id', householdId)
    .eq('is_default', true)
    .single();

  if (error) {
    // No default, get first
    const { data: first } = await supabase
      .from('dashboards')
      .select('*')
      .eq('household_id', householdId)
      .order('created_at')
      .limit(1)
      .single();
    return first;
  }

  return data;
}

export async function getDashboardForScreen(screenId: string): Promise<Dashboard | null> {
  const { data, error } = await supabase
    .from('dashboards')
    .select('*')
    .eq('screen_id', screenId)
    .single();

  if (error) {
    return null;
  }

  return data;
}

export async function createDashboard(
  householdId: string,
  dashboard: Partial<Dashboard>
): Promise<Dashboard | null> {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('dashboards')
    .insert({
      household_id: householdId,
      name: dashboard.name || 'New Dashboard',
      description: dashboard.description,
      icon: dashboard.icon || '📺',
      screen_id: dashboard.screen_id,
      is_default: dashboard.is_default || false,
      grid_columns: dashboard.grid_columns || 12,
      grid_row_height: dashboard.grid_row_height || 80,
      gap: dashboard.gap || 16,
      // Don't set background defaults - let the UI use theme colors
      background_type: dashboard.background_type || null,
      background_value: dashboard.background_value || null,
      created_by: user?.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to create dashboard:', error);
    return null;
  }

  return data;
}

export async function updateDashboard(
  dashboardId: string,
  updates: Partial<Dashboard>
): Promise<Dashboard | null> {
  const { data, error } = await supabase
    .from('dashboards')
    .update(updates)
    .eq('id', dashboardId)
    .select()
    .single();

  if (error) {
    console.error('Failed to update dashboard:', error);
    return null;
  }

  return data;
}

export async function deleteDashboard(dashboardId: string): Promise<boolean> {
  const { error } = await supabase
    .from('dashboards')
    .delete()
    .eq('id', dashboardId);

  return !error;
}

export async function duplicateDashboard(
  dashboardId: string,
  newName: string
): Promise<string | null> {
  const { data, error } = await supabase
    .rpc('duplicate_dashboard', {
      p_dashboard_id: dashboardId,
      p_new_name: newName,
    });

  if (error) {
    console.error('Failed to duplicate dashboard:', error);
    return null;
  }

  return data;
}

// ============================================
// WIDGET CRUD
// ============================================

export async function getWidgets(dashboardId: string): Promise<DashboardWidget[]> {
  const { data, error } = await supabase
    .from('dashboard_widgets')
    .select('*')
    .eq('dashboard_id', dashboardId)
    .order('grid_y')
    .order('grid_x');

  if (error) {
    console.error('Failed to get widgets:', error);
    return [];
  }

  return data || [];
}

export async function addWidget(
  dashboardId: string,
  widget: Partial<DashboardWidget>
): Promise<DashboardWidget | null> {
  const { data, error } = await supabase
    .from('dashboard_widgets')
    .insert({
      dashboard_id: dashboardId,
      widget_type: widget.widget_type,
      grid_x: widget.grid_x || 0,
      grid_y: widget.grid_y || 0,
      grid_w: widget.grid_w || 2,
      grid_h: widget.grid_h || 2,
      config: widget.config || {},
      title: widget.title,
      show_title: widget.show_title ?? true,
      background_color: widget.background_color,
      sort_order: widget.sort_order ?? 0,
      parent_widget_id: widget.parent_widget_id ?? null,
      column_index: widget.column_index ?? null,
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to add widget:', error);
    return null;
  }

  return data;
}

export async function updateWidget(
  widgetId: string,
  updates: Partial<DashboardWidget>
): Promise<DashboardWidget | null> {
  const { data, error } = await supabase
    .from('dashboard_widgets')
    .update(updates)
    .eq('id', widgetId)
    .select()
    .single();

  if (error) {
    console.error('Failed to update widget:', error);
    return null;
  }

  return data;
}

export async function updateWidgetPosition(
  widgetId: string,
  position: { grid_x: number; grid_y: number; grid_w: number; grid_h: number }
): Promise<boolean> {
  const { error } = await supabase
    .from('dashboard_widgets')
    .update(position)
    .eq('id', widgetId);

  return !error;
}

export async function updateWidgetPositions(
  updates: Array<{ id: string; grid_x: number; grid_y: number; grid_w: number; grid_h: number }>
): Promise<boolean> {
  // Batch update using transaction-like approach
  const promises = updates.map(({ id, ...position }) =>
    supabase.from('dashboard_widgets').update(position).eq('id', id)
  );

  const results = await Promise.all(promises);
  return results.every(r => !r.error);
}

export async function deleteWidget(widgetId: string): Promise<boolean> {
  const { error } = await supabase
    .from('dashboard_widgets')
    .delete()
    .eq('id', widgetId);

  return !error;
}

export async function setWidgetParent(
  widgetId: string,
  parentWidgetId: string | null,
  columnIndex: number | null = 0,
  sortOrder?: number
): Promise<boolean> {
  const updates: Partial<DashboardWidget> = {
    parent_widget_id: parentWidgetId,
    column_index: parentWidgetId ? (columnIndex ?? 0) : null,
  };

  if (sortOrder !== undefined) {
    updates.sort_order = sortOrder;
  }

  const { error } = await supabase
    .from('dashboard_widgets')
    .update(updates)
    .eq('id', widgetId);

  if (error) {
    console.error('Failed to set widget parent:', error);
    return false;
  }

  return true;
}

// Helper to get child widgets of a parent section
export function getChildWidgets(
  widgets: DashboardWidget[],
  parentId: string
): DashboardWidget[] {
  return widgets
    .filter(w => w.parent_widget_id === parentId)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

// Helper to get widgets for a specific column within a section
export function getWidgetsByColumn(
  widgets: DashboardWidget[],
  sectionId: string,
  columnIndex: number
): DashboardWidget[] {
  return widgets
    .filter(w => w.parent_widget_id === sectionId && (w.column_index ?? 0) === columnIndex)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

// Helper to get root widgets (no parent)
export function getRootWidgets(widgets: DashboardWidget[]): DashboardWidget[] {
  return widgets
    .filter(w => !w.parent_widget_id)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

// Helper to compute column widths array from section config
export function getColumnWidths(config: Partial<SectionConfig>): number[] {
  if (config.columns && config.columns.length > 0) {
    return config.columns;
  }
  const count = config.columnCount || 1;
  const width = Math.floor(12 / count);
  return Array(count).fill(width);
}

// Update widget column and sort order (for drag within section)
export async function updateWidgetColumn(
  widgetId: string,
  columnIndex: number,
  sortOrder: number
): Promise<boolean> {
  const { error } = await supabase
    .from('dashboard_widgets')
    .update({ column_index: columnIndex, sort_order: sortOrder })
    .eq('id', widgetId);

  if (error) {
    console.error('Failed to update widget column:', error);
    return false;
  }

  return true;
}

// Update sort order for multiple widgets (batch reorder)
export async function updateWidgetSortOrders(
  updates: Array<{ id: string; sort_order: number; column_index?: number }>
): Promise<boolean> {
  const promises = updates.map(({ id, sort_order, column_index }) => {
    const updateData: Partial<DashboardWidget> = { sort_order };
    if (column_index !== undefined) {
      updateData.column_index = column_index;
    }
    return supabase.from('dashboard_widgets').update(updateData).eq('id', id);
  });

  const results = await Promise.all(promises);
  return results.every(r => !r.error);
}

// ============================================
// WIDGET REGISTRY
// ============================================

export async function getWidgetDefinitions(): Promise<WidgetDefinition[]> {
  const { data, error } = await supabase
    .from('widget_registry')
    .select('*')
    .eq('is_active', true)
    .order('category')
    .order('name');

  if (error) {
    console.error('Failed to get widget definitions:', error);
    return [];
  }

  return data || [];
}

export async function getWidgetDefinition(widgetType: string): Promise<WidgetDefinition | null> {
  const { data, error } = await supabase
    .from('widget_registry')
    .select('*')
    .eq('id', widgetType)
    .single();

  if (error) {
    return null;
  }

  return data;
}

export function getWidgetsByCategory(
  definitions: WidgetDefinition[]
): Record<WidgetCategory, WidgetDefinition[]> {
  const categories: Record<WidgetCategory, WidgetDefinition[]> = {
    time: [],
    calendar: [],
    tasks: [],
    family: [],
    media: [],
    info: [],
    smart_home: [],
    travel: [],
    custom: [],
    layout: [],
  };

  definitions.forEach(def => {
    if (categories[def.category]) {
      categories[def.category].push(def);
    }
  });

  return categories;
}

// ============================================
// TEMPLATES
// ============================================

export async function getTemplates(): Promise<DashboardTemplate[]> {
  const { data, error } = await supabase
    .from('dashboard_templates')
    .select('*')
    .order('is_system', { ascending: false })
    .order('name');

  if (error) {
    console.error('Failed to get templates:', error);
    return [];
  }

  return data || [];
}

export async function createDashboardFromTemplate(
  householdId: string,
  templateId: string,
  name: string,
  screenId?: string
): Promise<string | null> {
  const { data, error } = await supabase
    .rpc('create_dashboard_from_template', {
      p_household_id: householdId,
      p_template_id: templateId,
      p_name: name,
      p_screen_id: screenId || null,
    });

  if (error) {
    console.error('Failed to create from template:', error);
    return null;
  }

  return data;
}

export async function saveAsTemplate(
  dashboardId: string,
  name: string,
  description?: string
): Promise<string | null> {
  // Get dashboard and widgets
  const dashboard = await getDashboard(dashboardId);
  const widgets = await getWidgets(dashboardId);
  
  if (!dashboard) return null;

  const { data, error } = await supabase
    .from('dashboard_templates')
    .insert({
      name,
      description,
      icon: dashboard.icon,
      category: 'custom',
      grid_columns: dashboard.grid_columns,
      grid_row_height: dashboard.grid_row_height,
      background_type: dashboard.background_type,
      background_value: dashboard.background_value,
      widgets: widgets.map(w => ({
        widget_type: w.widget_type,
        grid_x: w.grid_x,
        grid_y: w.grid_y,
        grid_w: w.grid_w,
        grid_h: w.grid_h,
        config: w.config,
        title: w.title,
      })),
      is_system: false,
    })
    .select('id')
    .single();

  if (error) {
    console.error('Failed to save template:', error);
    return null;
  }

  return data.id;
}

// ============================================
// REAL-TIME SUBSCRIPTIONS
// ============================================

export function subscribeToDashboard(
  dashboardId: string,
  onUpdate: (dashboard: Dashboard) => void
): () => void {
  const subscription = supabase
    .channel(`dashboard:${dashboardId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'dashboards',
        filter: `id=eq.${dashboardId}`,
      },
      (payload) => {
        if (payload.new) {
          onUpdate(payload.new as Dashboard);
        }
      }
    )
    .subscribe();

  return () => {
    subscription.unsubscribe();
  };
}

export function subscribeToWidgets(
  dashboardId: string,
  onUpdate: (widgets: DashboardWidget[]) => void
): () => void {
  const subscription = supabase
    .channel(`widgets:${dashboardId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'dashboard_widgets',
        filter: `dashboard_id=eq.${dashboardId}`,
      },
      async () => {
        // Refetch all widgets on any change
        const widgets = await getWidgets(dashboardId);
        onUpdate(widgets);
      }
    )
    .subscribe();

  return () => {
    subscription.unsubscribe();
  };
}

// ============================================
// SLIDESHOW BACKGROUND FUNCTIONS
// ============================================

/**
 * Get cached slideshow photos for a dashboard
 */
export async function getSlideshowPhotos(dashboardId: string): Promise<SlideshowPhoto[]> {
  const { data, error } = await supabase
    .rpc('get_slideshow_photos', { p_dashboard_id: dashboardId });

  if (error) {
    console.error('Error getting slideshow photos:', error);
    return [];
  }

  return data || [];
}

/**
 * Fetch photos via edge function (handles Google Photos API securely)
 * This will fetch from Google Photos or local albums and cache the results
 */
export async function fetchSlideshowPhotos(
  dashboardId: string,
  householdId: string,
  config: SlideshowConfig
): Promise<SlideshowPhoto[]> {
  try {
    const { data, error } = await supabase.functions.invoke('fetch-google-photos', {
      body: {
        dashboardId,
        householdId,
        albumId: config.album_id,
        source: config.source,
        pageSize: 100,
      },
    });

    if (error) {
      console.error('Error fetching slideshow photos:', error);
      // Fall back to cached photos
      return getSlideshowPhotos(dashboardId);
    }

    if (!data.success) {
      console.error('Fetch photos failed:', data.error);
      // Fall back to cached photos
      return getSlideshowPhotos(dashboardId);
    }

    // Return the fetched photos (they're already cached by the edge function)
    return (data.photos || []).map((p: any, idx: number) => ({
      photo_url: p.url,
      thumbnail_url: p.thumbnail_url,
      sort_order: idx,
    }));
  } catch (err) {
    console.error('Error invoking fetch-google-photos:', err);
    // Fall back to cached photos
    return getSlideshowPhotos(dashboardId);
  }
}

/**
 * Refresh the slideshow cache with new photos
 */
export async function refreshSlideshowCache(
  dashboardId: string,
  photos: Array<{
    url: string;
    thumbnail_url?: string;
    google_photo_id?: string;
    local_photo_id?: string;
  }>
): Promise<number> {
  const { data, error } = await supabase
    .rpc('refresh_slideshow_cache', {
      p_dashboard_id: dashboardId,
      p_photos: photos,
    });

  if (error) {
    console.error('Error refreshing slideshow cache:', error);
    return 0;
  }

  return data || 0;
}

/**
 * Update slideshow configuration for a dashboard
 */
export async function updateSlideshowConfig(
  dashboardId: string,
  config: Partial<SlideshowConfig>
): Promise<boolean> {
  const { error } = await supabase
    .from('dashboards')
    .update({
      background_type: 'slideshow',
      background_slideshow_config: config,
    })
    .eq('id', dashboardId);

  if (error) {
    console.error('Error updating slideshow config:', error);
    return false;
  }

  return true;
}

// ============================================
// LOCAL PHOTO ALBUMS
// ============================================

export interface LocalPhotoAlbum {
  id: string;
  name: string;
  photo_count: number;
}

export async function getLocalPhotoAlbums(householdId: string): Promise<LocalPhotoAlbum[]> {
  const { data, error } = await supabase
    .from('photo_albums')
    .select(`
      id,
      name,
      photos:photos(count)
    `)
    .eq('household_id', householdId)
    .order('name');

  if (error) {
    console.error('Error fetching local photo albums:', error);
    return [];
  }

  return (data || []).map(album => ({
    id: album.id,
    name: album.name,
    photo_count: (album.photos as any)?.[0]?.count || 0,
  }));
}

/**
 * Create a new local photo album
 */
export async function createPhotoAlbum(
  householdId: string,
  name: string
): Promise<LocalPhotoAlbum | null> {
  const { data, error } = await supabase
    .from('photo_albums')
    .insert({
      household_id: householdId,
      name: name.trim(),
      source: 'local',
      is_active: true,
    })
    .select('id, name')
    .single();

  if (error) {
    console.error('Error creating photo album:', error);
    return null;
  }

  return {
    id: data.id,
    name: data.name,
    photo_count: 0,
  };
}

/**
 * Update an existing photo album
 */
export async function updatePhotoAlbum(
  albumId: string,
  name: string
): Promise<boolean> {
  const { error } = await supabase
    .from('photo_albums')
    .update({ name: name.trim() })
    .eq('id', albumId);

  if (error) {
    console.error('Error updating photo album:', error);
    return false;
  }

  return true;
}

/**
 * Delete a photo album and all its photos
 */
export async function deletePhotoAlbum(albumId: string): Promise<boolean> {
  const { error } = await supabase
    .from('photo_albums')
    .delete()
    .eq('id', albumId);

  if (error) {
    console.error('Error deleting photo album:', error);
    return false;
  }

  return true;
}
